// src/hooks/useSettingsStore.ts
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { userService, ProfileUpdatePayload } from "../services/userService";

/* =========================
   Types exported for pages
   ========================= */
export type PaymentBrand = "visa" | "mastercard" | "amex" | "unknown";

export type PaymentCard = {
  id: string;
  holderName: string;
  last4: string;
  brand: PaymentBrand;
  expMonth: string; // "MM"
  expYear: string; // "YY"
};

export type BankAccount = {
  holderName: string;
  iban: string;
  addressLine?: string;
};

export type ShippingAddress = {
  fullName: string;
  street: string;
  houseNumber: string;
  box?: string;
  postalCode: string;
  city: string;
  country: string;
};

export type ProfileSettingsData = {
  photoDataUrl: string | null;
  username: string; // comes from auth by default
  about: string; // local only
  country: string;
  city: string;
  showLocation: boolean;
  language: string;
};

export type AccountSettingsData = {
  email: string; // comes from auth by default
  emailVerified: boolean;

  phone: string; // local until backend
  phoneVerified: boolean;

  firstName: string; // comes from auth if available, else local
  lastName: string;

  gender: "" | "Man" | "Vrouw" | "Anders" | "Zeg ik liever niet";
  birthDate: string; // yyyy-mm-dd

  connectedGoogle: boolean;
  connectedFacebook: boolean;
};

export type ShippingSettingsData = {
  address: ShippingAddress | null;
};

export type PaymentsSettingsData = {
  cards: PaymentCard[];
  bank: BankAccount | null;
};

export type NotificationsSettingsData = {
  emailEnabled: boolean;

  // EN keys
  marketing: boolean;
  newMessages: boolean;
  newFeedback: boolean;
  auctionUpdates: boolean;
  vintedUpdates: boolean;

  // NL keys (for your old pages)
  marketingBerichten: boolean;
  nieuweBerichten: boolean;
  nieuweFeedback: boolean;
  veilingUpdates: boolean;

  afgeprijsdeArtikelen: boolean;
};

export type PrivacySettingsData = {
  profileVisibility: "Iedereen" | "Alleen ingelogd" | "Niemand";
  searchable: boolean;

  marketingTrackingConsent: boolean;
  cookiesPersonalisation: boolean;

  whoCanMessageMe: "Iedereen" | "Alleen volgers" | "Niemand";

  // aliases (so your page doesn’t break)
  allowMessagesFrom: "everyone" | "members" | "noone";
  marketingConsent: boolean;
  trackingConsent: boolean;
  cookiePersonalisation: boolean;
};

export type SecuritySettingsData = {
  twoFactorEnabled: boolean;
};

export type SettingsState = {
  profile: ProfileSettingsData;
  account: AccountSettingsData;
  shipping: ShippingSettingsData;
  payments: PaymentsSettingsData;
  notifications: NotificationsSettingsData;
  privacy: PrivacySettingsData;
  security: SecuritySettingsData;
};

/* =========================
   Helpers
   ========================= */
function safeParse(raw: string | null) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function toBool(v: unknown, fallback: boolean): boolean {
  return typeof v === "boolean" ? v : fallback;
}

function normalize(state: SettingsState): SettingsState {
  // ensure EN<->NL notification sync
  const n = state.notifications;

  n.emailEnabled = !!n.emailEnabled;

  // EN
  n.marketing = !!n.marketing;
  n.newMessages = !!n.newMessages;
  n.newFeedback = !!n.newFeedback;
  n.auctionUpdates = !!n.auctionUpdates;
  n.vintedUpdates = !!n.vintedUpdates;

  // NL (always booleans)
  n.marketingBerichten = toBool(n.marketingBerichten, n.marketing);
  n.nieuweBerichten = toBool(n.nieuweBerichten, n.newMessages);
  n.nieuweFeedback = toBool(n.nieuweFeedback, n.newFeedback);
  n.veilingUpdates = toBool(n.veilingUpdates, n.auctionUpdates);
  n.afgeprijsdeArtikelen = !!n.afgeprijsdeArtikelen;

  // pick NL as source of truth (so your old UI keeps working)
  n.marketing = n.marketingBerichten;
  n.newMessages = n.nieuweBerichten;
  n.newFeedback = n.nieuweFeedback;
  n.auctionUpdates = n.veilingUpdates;

  // privacy aliases sync
  const p = state.privacy;

  p.marketingConsent = toBool(p.marketingConsent, p.marketingTrackingConsent);
  p.trackingConsent = toBool(p.trackingConsent, p.marketingTrackingConsent);
  p.cookiePersonalisation = toBool(
    p.cookiePersonalisation,
    p.cookiesPersonalisation,
  );

  p.marketingTrackingConsent = toBool(
    p.marketingTrackingConsent,
    p.marketingConsent && p.trackingConsent,
  );
  p.cookiesPersonalisation = toBool(
    p.cookiesPersonalisation,
    p.cookiePersonalisation,
  );

  if (p.allowMessagesFrom === "everyone") p.whoCanMessageMe = "Iedereen";
  else if (p.allowMessagesFrom === "members")
    p.whoCanMessageMe = "Alleen volgers";
  else p.whoCanMessageMe = "Niemand";

  if (p.whoCanMessageMe === "Iedereen") p.allowMessagesFrom = "everyone";
  else if (p.whoCanMessageMe === "Alleen volgers")
    p.allowMessagesFrom = "members";
  else p.allowMessagesFrom = "noone";

  // harden arrays/nullables
  state.payments.cards = Array.isArray(state.payments.cards)
    ? state.payments.cards
    : [];
  state.payments.bank = state.payments.bank ?? null;
  state.shipping.address = state.shipping.address ?? null;

  return state;
}

/* =========================
   Default WITHOUT hardcoded user
   ========================= */
function makeDefaultState(username: string, email: string): SettingsState {
  return normalize({
    profile: {
      photoDataUrl: null,
      username: username || "user",
      about: "",
      country: "België",
      city: "",
      showLocation: true,
      language: "Nederlands (Dutch)",
    },

    account: {
      email: email || "",
      emailVerified: false,

      phone: "",
      phoneVerified: false,

      firstName: "",
      lastName: "",

      gender: "",
      birthDate: "",

      connectedGoogle: false,
      connectedFacebook: false,
    },

    shipping: { address: null },

    payments: { cards: [], bank: null },

    notifications: {
      emailEnabled: true,

      marketing: false,
      newMessages: true,
      newFeedback: true,
      auctionUpdates: true,
      vintedUpdates: true,

      marketingBerichten: false,
      nieuweBerichten: true,
      nieuweFeedback: true,
      veilingUpdates: true,

      afgeprijsdeArtikelen: true,
    },

    privacy: {
      profileVisibility: "Iedereen",
      searchable: true,
      marketingTrackingConsent: true,
      cookiesPersonalisation: true,
      whoCanMessageMe: "Iedereen",

      allowMessagesFrom: "everyone",
      marketingConsent: true,
      trackingConsent: true,
      cookiePersonalisation: true,
    },

    security: { twoFactorEnabled: false },
  });
}

/* =========================
   Hook
   ========================= */
export function useSettingsStore() {
  const { user, isAuthed } = useAuth();

  // create a user-scoped key so different accounts don't overwrite each other
  const userKey = useMemo(() => {
    const id = user?.username || "guest";
    return `fsm_settings_v3_${id}`;
  }, [user]);

  const authUsername = user?.username || "guest";
  const authEmail = user?.email || ""; // if your auth context has email later

  const [state, setState] = useState<SettingsState>(() =>
    makeDefaultState(authUsername, authEmail),
  );
  const [loaded, setLoaded] = useState(false);

  // load from localStorage on login change
  useEffect(() => {
    const base = makeDefaultState(authUsername, authEmail);
    const parsed = safeParse(localStorage.getItem(userKey));
    const merged = parsed ? normalize({ ...base, ...parsed }) : base;
    setState(merged);
    setLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey, authUsername, authEmail, isAuthed]);

  // ALWAYS sync auth -> state (so username/email don't "disappear")
  useEffect(() => {
    if (!loaded) return;
    if (!isAuthed || !user) return;

    setState((prev) =>
      normalize({
        ...prev,
        profile: {
          ...prev.profile,
          username: user.username || prev.profile.username,
        },
        account: {
          ...prev.account,
          email: user.email || prev.account.email,
          firstName: prev.account.firstName,
          lastName: prev.account.lastName,
        },
      }),
    );
  }, [loaded, isAuthed, user]);

  const save = useCallback(
    (next: SettingsState) => {
      const normalized = normalize({ ...next });
      setState(normalized);
      localStorage.setItem(userKey, JSON.stringify(normalized));
    },
    [userKey],
  );

  /** Build a ProfileUpdatePayload from current state for the backend */
  const buildPayload = useCallback(
    (s: SettingsState): ProfileUpdatePayload => {
      const addr = s.shipping.address;
      return {
        firstname: s.account.firstName || null,
        lastname: s.account.lastName || null,
        username: s.profile.username || null,
        email: s.account.email || null,
        street: addr?.street || null,
        number: addr?.houseNumber || null,
        box: addr?.box || null,
        zip: addr?.postalCode || null,
        city: addr?.city || s.profile.city || null,
        country: addr?.country || s.profile.country || null,
        picture: s.profile.photoDataUrl || null,
        bio: s.profile.about || null,
        geo_hide: !s.profile.showLocation,
        language: s.profile.language || null,
        iban: s.payments.bank?.iban || null,
        bic: null,
      };
    },
    [],
  );

  /**
   * Save to backend via POST /auth/profile.
   * Accepts an optional partial payload to send only specific fields.
   * Returns the backend response or throws on error.
   */
  const saveToBackend = useCallback(
    async (partial?: Partial<ProfileUpdatePayload>) => {
      const payload = partial ?? buildPayload(state);
      return userService.updateProfile(payload);
    },
    [state, buildPayload],
  );

  const api = useMemo(() => {
    return {
      loaded,
      state,
      saveToBackend,

      setProfile(partial: Partial<ProfileSettingsData>) {
        save({
          ...state,
          profile: { ...state.profile, ...partial },
        });
      },

      setAccount(partial: Partial<AccountSettingsData>) {
        save({
          ...state,
          account: { ...state.account, ...partial },
        });
      },

      setShipping(partial: Partial<ShippingSettingsData>) {
        save({
          ...state,
          shipping: { ...state.shipping, ...partial },
        });
      },

      setPayments(partial: Partial<PaymentsSettingsData>) {
        save({
          ...state,
          payments: { ...state.payments, ...partial },
        });
      },

      setNotifications(partial: Partial<NotificationsSettingsData>) {
        save({
          ...state,
          notifications: { ...state.notifications, ...partial },
        });
      },

      setPrivacy(partial: Partial<PrivacySettingsData>) {
        save({
          ...state,
          privacy: { ...state.privacy, ...partial },
        });
      },

      setSecurity(partial: Partial<SecuritySettingsData>) {
        save({
          ...state,
          security: { ...state.security, ...partial },
        });
      },

      reset() {
        const base = makeDefaultState(authUsername, authEmail);
        save(base);
      },
    };
  }, [state, loaded, save, saveToBackend, authUsername, authEmail]);

  return api;
}
